import React from 'react'
import {
  Badge,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '../../utils/chakraMonomorphic'

import {
  usePhotoPersonTags,
  TaggedCamper,
  UsePhotoPersonTagsState,
} from '../../hooks/usePhotoPersonTags'
// Same placeholder the album grid falls back to, so a photo whose URL 404s or
// whose signed URL has expired does not render a broken-image icon. Imported
// here rather than passed down so the modal cannot be mounted without it.
import fallbackImage from '../../assets/images/placeholderPhoto.jpg'

const MODAL_HEADING = 'Tagged Campers'
const EMPTY_COPY = 'No campers are tagged in this photo.'
const MANUAL_LABEL = 'Manual'
const FACE_FINDER_LABEL = 'Face Finder'
// Chakra resolves these to CSS custom properties, which jsdom does not compute,
// so the badge tests can only assert the label — keeping the mapping named here
// makes an accidental swap reviewable in one place instead of inline.
const MANUAL_COLOR_SCHEME = 'green'
const FACE_FINDER_COLOR_SCHEME = 'purple'
const TAGGED_CAMPER_TEST_ID_PREFIX = 'tagged-camper-'
const TAG_SOURCE_TEST_ID_PREFIX = 'tag-source-'

export interface PhotoTagModalProps {
  isOpen: boolean
  onClose: () => void
  photoId: number
  clientId: number
  photoUrl: string
}

const TaggedRow: React.FC<Readonly<TaggedCamper>> = ({
  personId,
  displayName,
  manual,
  taggedByName,
}) => (
  <Flex
    direction='column'
    py={1}
    data-testid={`${TAGGED_CAMPER_TEST_ID_PREFIX}${personId}`}>
    <Flex alignItems='center'>
      <Text mr={2}>{displayName}</Text>
      <Badge
        colorScheme={manual ? MANUAL_COLOR_SCHEME : FACE_FINDER_COLOR_SCHEME}
        data-testid={`${TAG_SOURCE_TEST_ID_PREFIX}${personId}`}>
        {manual ? MANUAL_LABEL : FACE_FINDER_LABEL}
      </Badge>
    </Flex>
    {/* Manual rows only. This reflects a photos-service CONVENTION, not an
        enforced invariant: ListPhotoPersonTags derives `manual` from
        `Confidence IS NULL` while selecting `taggingpersonid` as an independent
        column, so a row carrying both a confidence and a tagger is
        representable. Should one appear, its tagger is intentionally not shown
        here — the "Face Finder" badge would contradict it. */}
    {manual && taggedByName && (
      <Text fontSize='sm' color='gray.500'>
        tagged by {taggedByName}
      </Text>
    )}
  </Flex>
)

const TagsBody: React.FC<Readonly<UsePhotoPersonTagsState>> = ({
  status,
  tags,
  errorMessage,
}) => {
  if (status === 'rejected') {
    return (
      <Text color='red.500' data-testid='tags-error'>
        {errorMessage}
      </Text>
    )
  }

  if (status === 'resolved' && tags.length === 0) {
    return (
      <Text color='gray.500' data-testid='tags-empty'>
        {EMPTY_COPY}
      </Text>
    )
  }

  if (status === 'resolved') {
    return (
      <>
        {tags.map((tag) => (
          <TaggedRow key={tag.personId} {...tag} />
        ))}
      </>
    )
  }

  // 'idle' and 'pending'. The tag list is guarded on 'resolved' above rather
  // than reached by fall-through, so the pre-effect 'idle' frame (the hook's
  // effect runs after the first paint) shows the spinner instead of an empty
  // body — and a future FetchStatus member cannot silently render as "no tags".
  return (
    <Flex justifyContent='center' py={4} data-testid='tags-loading'>
      <Spinner size='sm' />
    </Flex>
  )
}

/**
 * Read-only view of a photo's person tags: who is tagged, and whether Face
 * Finder or a human placed the tag. No add/remove affordance — this is a
 * deliberately stripped-down sibling of the write-capable tagging modal that
 * lives on `feat/23384-manual-photo-tagging`.
 */
export const PhotoTagModal: React.FC<Readonly<PhotoTagModalProps>> = ({
  isOpen,
  onClose,
  photoId,
  clientId,
  photoUrl,
}) => {
  const {status, tags, errorMessage} = usePhotoPersonTags({
    photoId,
    clientId,
    enabled: isOpen,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{MODAL_HEADING}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Image
            maxH='240px'
            mb={4}
            mx='auto'
            alt={`Photo ${photoId}`}
            src={photoUrl}
            fallbackSrc={fallbackImage}
          />
          <TagsBody status={status} tags={tags} errorMessage={errorMessage} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
